import { FC, useEffect, useState, useRef } from 'react';
import clsx from 'clsx';
import { Button, DropDownModal, getNextDateObject } from '@marriott/mi-ui-library';
import { SearchFormProps, SearchFormData } from '../SearchForm.types';
import { StyledSearchFormMobile } from './SearchFormMobile.styles';
import { SearchFormFieldNew } from './SearchFormMobile.types';
import { useSearchFormStore, useLocaleStore } from '../../../store';
import { TextFormField, MobileCalendar } from '../../../molecules';
import { Variant } from '../../../molecules/RoomsAndEventSpace/RoomsAndEventSpace.types';
import { Destination } from '../Destination';
import { SearchFormExpandedMobile } from './SearchFormExpandedMobile';
import {
  setCalendarOptions,
  getFormattedDateObject,
  getFormattedDatesObject,
  getFormattedDates,
  getFormattedDate,
  validateRoomsAndEventSpaceMobile,
  isToday,
} from '../../../utils';
import { CALENDAR_START_DATE } from '../../../constants';
import type { SelectedDateType } from '../../../molecules/DatePicker/DatePicker.types';
import { RoomsAndEventSpace } from '../../../molecules/RoomsAndEventSpace';
import { CalendarTabValue } from '../../../molecules/Calendar/Calendar.types';
import { RoomsAndEventSpaceFieldErrors } from '../../../molecules/RoomsAndEventSpace/RoomsAndEventSpace.types';

export const SearchFormMobile: FC<SearchFormProps> = props => {
  const {
    destination,
    dates,
    guestRooms,
    attendees,
    guestRoomsAndEventSpace,
    ctas,
    isSearchResultPage,
    searchResultsFormData,
    isSticky,
    mobileHeading,
    onSubmit,
  } = props;
  const { shortWeekdayNames, longWeekdayNames, shortMonthNames, longMonthNames } = dates;

  const {
    currentStep,
    setCurrentStep,
    setDefaultMobileView,
    formData,
    persistedFormData,
    setFormData,
    setPersistedFormData,
    resetFormData,
    resetPersistedFormData,
  } = useSearchFormStore();

  const { locale } = useLocaleStore();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [visitedSteps, setVisitedSteps] = useState<SearchFormFieldNew[]>([]);
  const [destinationOnFocus, setDestinationOnFocus] = useState(false);
  const [, setSelectedDateType] = useState<SelectedDateType | undefined>(CALENDAR_START_DATE);
  const [calendarTab, setCalendarTab] = useState<CalendarTabValue>(CalendarTabValue.MULTIPLE);
  const [fieldErrors, setFieldErrors] = useState<RoomsAndEventSpaceFieldErrors>({});
  const [dateError, setDateError] = useState('');

  const prevCalendarTabRef = useRef<CalendarTabValue>(CalendarTabValue.MULTIPLE);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const roomsVariant = calendarTab === CalendarTabValue.SINGLE ? Variant.EVENTSPACE : Variant.ROOMSANDEVENTSPACE;
  const headers = { 'accept-language': locale };

  useEffect(() => {
    document.body.style.overflow = isModalOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isModalOpen]);

  useEffect(() => {
    setCalendarOptions({
      shortWeekdayNames,
      longWeekdayNames,
      shortMonthNames,
      longMonthNames,
    });
  }, [shortWeekdayNames, longWeekdayNames, shortMonthNames, longMonthNames]);

  useEffect(() => {
    if (currentStep !== SearchFormFieldNew.INITIAL) {
      setVisitedSteps(prev => [...prev, currentStep as SearchFormFieldNew]);
    }
    if (currentStep === SearchFormFieldNew.DATES) {
      prevCalendarTabRef.current = calendarTab;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional snapshot of calendarTab on step entry
  }, [currentStep]);

  useEffect(() => {
    setDefaultMobileView(true);
    if (isSearchResultPage && searchResultsFormData) {
      const { startDate, endDate } = getFormattedDatesObject(
        searchResultsFormData?.startDate,
        searchResultsFormData?.endDate
      );
      const formattedDates = getFormattedDates(
        getFormattedDateObject(searchResultsFormData?.startDate),
        getFormattedDateObject(searchResultsFormData?.endDate),
        'dateWithMonth'
      );

      const updatedFormData = {
        ...formData,
        destination: persistedFormData.destination?.description
          ? persistedFormData.destination
          : searchResultsFormData.destination,
        dates: persistedFormData.dates || formattedDates,
        startDate: persistedFormData.startDate || startDate,
        endDate: persistedFormData.endDate || endDate,
        guestRooms: persistedFormData.guestRooms || searchResultsFormData.guestRooms,
        attendees: persistedFormData.attendees || searchResultsFormData.attendees,
      };

      setFormData(updatedFormData);
      setPersistedFormData(updatedFormData);
      setCurrentStep(SearchFormFieldNew.INITIAL);

      if (updatedFormData.startDate && updatedFormData.startDate === updatedFormData.endDate) {
        setCalendarTab(CalendarTabValue.SINGLE);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setFieldErrors({});
  }, [roomsVariant]);

  const updateFormData = (value: Partial<typeof formData>) => {
    setFormData(value);
  };

  const goToNextStep = (step: SearchFormFieldNew) => {
    const summaryVisited = visitedSteps.includes(SearchFormFieldNew.SUMMARY);

    if (summaryVisited) {
      setCurrentStep(SearchFormFieldNew.SUMMARY);
    } else if (step === SearchFormFieldNew.DESTINATION) {
      setCurrentStep(SearchFormFieldNew.DATES);
    } else if (step === SearchFormFieldNew.DATES) {
      setCurrentStep(SearchFormFieldNew.ROOMS_AND_EVENTSPACE);
    } else if (step === SearchFormFieldNew.ROOMS_AND_EVENTSPACE) {
      setCurrentStep(SearchFormFieldNew.SUMMARY);
    }
  };

  const reset = () => {
    setCurrentStep(SearchFormFieldNew.INITIAL);
    setDefaultMobileView(true);
    setVisitedSteps([]);
    resetFormData();
    setSelectedDateType(CALENDAR_START_DATE);
    setCalendarTab(CalendarTabValue.MULTIPLE);
    setFieldErrors({});
    if (!isSearchResultPage) {
      resetPersistedFormData();
    }
  };

  const handleOpenModal = (step: SearchFormFieldNew = SearchFormFieldNew.DESTINATION) => {
    const openStep = isSearchResultPage ? SearchFormFieldNew.SUMMARY : step;
    setCurrentStep(openStep);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    if (isSearchResultPage) {
      setCurrentStep(SearchFormFieldNew.INITIAL);
      setDefaultMobileView(true);
      setFieldErrors({});
      setFormData({ ...persistedFormData });
    } else {
      reset();
    }
    setDestinationOnFocus(false);
  };

  const handleFind = (data: SearchFormData) => {
    setPersistedFormData(data);
    onSubmit(data);
    setIsModalOpen(false);
    setCurrentStep(SearchFormFieldNew.INITIAL);
    setDefaultMobileView(true);
    setVisitedSteps([]);
    setFieldErrors({});
    setDestinationOnFocus(false);
  };

  const updateDestination = (value: { destination: { description: string; placeId?: string } }) => {
    setDestinationOnFocus(false);
    updateFormData(value);
    setPersistedFormData({ ...persistedFormData, ...value });
    setTimeout(() => goToNextStep(SearchFormFieldNew.DESTINATION), 100);
  };

  const isTodaySelected = () => {
    return (formData.startDate && isToday(formData.startDate)) || (formData.endDate && isToday(formData.endDate));
  };

  const handleDatesApply = () => {
    const isMultipleDaysSelected = calendarTab === CalendarTabValue.MULTIPLE;
    if (!formData.startDate) {
      setDateError(dates.requiredError);
      return;
    }
    if (isTodaySelected()) {
      setDateError(dates.currentDateError || '');
      return;
    }
    setDateError('');

    let updatedDates = formData.dates;
    let updatedEndDate = formData.endDate;

    if (isMultipleDaysSelected && formData.startDate && !formData.endDate) {
      const formattedStartDate = getFormattedDateObject(formData.startDate, 'dateWithDayMonthAndYear');
      const newEndDate = getNextDateObject(formattedStartDate);
      updatedDates = getFormattedDates(formattedStartDate, newEndDate, 'dateWithMonth');
      updatedEndDate = getFormattedDate(newEndDate, 'dateWithDayMonthAndYear');
      updateFormData({ dates: updatedDates, endDate: updatedEndDate });
    }

    setPersistedFormData({
      ...persistedFormData,
      dates: updatedDates,
      startDate: formData.startDate,
      endDate: updatedEndDate,
    });

    const summaryVisited = visitedSteps.includes(SearchFormFieldNew.SUMMARY);
    const dateSelectionTypeChanged = prevCalendarTabRef.current !== calendarTab;

    if (summaryVisited && dateSelectionTypeChanged) {
      setCurrentStep(SearchFormFieldNew.ROOMS_AND_EVENTSPACE);
      return;
    }

    goToNextStep(SearchFormFieldNew.DATES);
  };

  const handleCalendarChange = (dates: string, startDate: string, endDate: string) => {
    setDateError('');
    updateFormData({
      dates,
      startDate,
      endDate,
    });
  };

  const handleCalendarTabChange = (nextTab: CalendarTabValue) => {
    setCalendarTab(nextTab);
    updateFormData({ dates: '', startDate: '', endDate: '' });

    if (nextTab === CalendarTabValue.SINGLE) {
      updateFormData({ guestRooms: '' });
      setPersistedFormData({
        ...persistedFormData,
        guestRooms: '',
      });
    }
  };

  const handleRoomsAndEventSpaceChange = (value: { rooms: number; eventAttendees: number }) => {
    setFieldErrors({});
    updateFormData({
      guestRooms: value.rooms.toString(),
      attendees: value.eventAttendees.toString(),
    });
  };

  const handleRoomsAndEventSpaceDone = () => {
    const errors = validateRoomsAndEventSpaceMobile(+formData.guestRooms || 0, +formData.attendees || 0, roomsVariant, {
      guestRoomsRequiredError: guestRooms.mobileRequiredError,
      guestRoomsMinCountError: guestRooms.minCountError,
      attendeesRequiredError: attendees.requiredError,
      attendeesMinCountError: attendees.mobileRequiredError,
    });

    if (errors.rooms || errors.attendees) {
      setFieldErrors(errors);
      return;
    }

    setFieldErrors({});
    setPersistedFormData({
      ...persistedFormData,
      guestRooms: formData.guestRooms,
      attendees: formData.attendees,
    });
    goToNextStep(SearchFormFieldNew.ROOMS_AND_EVENTSPACE);
  };

  const destinationField = (
    <TextFormField
      placeholder={destination.mobilePlaceholder}
      ariaLabel={destination.ariaLabel}
      className="m-input-field"
      inputTextFieldClassname="input-search-box"
      iconClass="icon-search"
      iconPosition="leading"
      showIcon={true}
      value={persistedFormData.destination.description ?? searchResultsFormData?.destination?.description}
      getInputProps={() => ({
        readOnly: true,
      })}
      onClick={() => handleOpenModal(SearchFormFieldNew.DESTINATION)}
    />
  );

  const renderDestinationStep = () => (
    <div>
      <div className="modal-destination-field">
        <TextFormField
          label={destination.label}
          placeholder={destination.placeholder}
          ariaLabel={destination.ariaLabel}
          className="m-input-text-field"
          value={formData.destination.description}
          onChange={e => updateFormData({ destination: { description: e.target.value, placeId: '' } })}
          showIcon={destinationOnFocus && !!formData.destination.description}
          iconClass="icon icon-s icon-cancel"
          iconAriaLabel={`${ctas.reset} ${destination.label}`}
          iconClickHandler={() => updateFormData({ destination: { description: '', placeId: '' } })}
          getInputProps={() => ({
            onFocus: () => setDestinationOnFocus(true),
            onBlur: () => setTimeout(() => setDestinationOnFocus(false), 0),
          })}
        />
      </div>
      <Destination
        currentValue={formData.destination.description}
        labels={destination}
        customHeaders={headers}
        updateFormData={updateDestination}
        isInputFocused={destinationOnFocus}
      />
    </div>
  );

  const renderCalendarStep = () => (
    <MobileCalendar
      dates={dates}
      ctas={ctas}
      selectedStartDate={
        formData.startDate ? getFormattedDateObject(formData.startDate, 'dateWithDayMonthAndYear') : null
      }
      selectedEndDate={formData.endDate ? getFormattedDateObject(formData.endDate, 'dateWithDayMonthAndYear') : null}
      calendarTab={calendarTab}
      onCalendarTabChange={handleCalendarTabChange}
      onChange={handleCalendarChange}
      hideDatesBadge={true}
      showErrorMessage={!!dateError}
      errorMessage={dateError}
    />
  );

  const renderRoomsAndEventSpaceStep = () => (
    <div className="modal-rooms-eventspace">
      <RoomsAndEventSpace
        variant={roomsVariant}
        isMobile={true}
        value={{
          rooms: +formData?.guestRooms || 0,
          eventAttendees: +formData?.attendees || 0,
        }}
        onChange={handleRoomsAndEventSpaceChange}
        fieldErrors={fieldErrors}
        labels={{
          guestRooms,
          attendees,
          guestRoomsAndEventSpace,
          reset: ctas.reset,
          resetAriaLabel: ctas.resetAriaLabel,
        }}
      />
    </div>
  );

  const renderModalHeading = () => {
    switch (currentStep) {
      case SearchFormFieldNew.DESTINATION:
        return destination.mobileHeading;
      case SearchFormFieldNew.DATES:
        return dates.mobileHeading;
      case SearchFormFieldNew.ROOMS_AND_EVENTSPACE:
        return calendarTab === CalendarTabValue.MULTIPLE ? guestRoomsAndEventSpace.label : attendees.label;
      case SearchFormFieldNew.SUMMARY:
        return mobileHeading;
      default:
        return '';
    }
  };

  const renderModalContent = () => {
    switch (currentStep) {
      case SearchFormFieldNew.DESTINATION:
        return renderDestinationStep();
      case SearchFormFieldNew.DATES:
        return renderCalendarStep();
      case SearchFormFieldNew.ROOMS_AND_EVENTSPACE:
        return renderRoomsAndEventSpaceStep();
      case SearchFormFieldNew.SUMMARY:
        return <SearchFormExpandedMobile labels={props} onSubmit={handleFind} />;
      default:
        return null;
    }
  };

  const renderModalFooter = () => {
    const callbackMap: Partial<Record<SearchFormFieldNew, () => void>> = {
      [SearchFormFieldNew.DATES]: handleDatesApply,
      [SearchFormFieldNew.ROOMS_AND_EVENTSPACE]: handleRoomsAndEventSpaceDone,
    };
    const callback = callbackMap[currentStep as SearchFormFieldNew];
    if (!callback) return null;
    return (
      <div className="modal-common-footer">
        <Button
          buttonCopy={ctas.apply}
          className={clsx('m-button-m', 'm-button-secondary', 'apply-btn', 'mr-1')}
          callback={callback}
          ref={buttonRef}
          ariaLabel={ctas.applyAriaLabel}
        />
      </div>
    );
  };

  return (
    <StyledSearchFormMobile isSticky={isSticky} isSearchResultPage={isSearchResultPage}>
      {destinationField}
      <DropDownModal
        className="search-form-mobile-modal"
        role="dialog"
        show={isModalOpen}
        dropdownModalOpenState={isModalOpen}
        setDropdownModalOpenState={setIsModalOpen}
        mobileTakeOver={true}
        mobileHeaderEnabled={true}
        mobileModalHeading={renderModalHeading()}
        dropdownModalOnCLoseFunc={handleCloseModal}
        customLastElementRef={buttonRef}
      >
        <div className="modal-content-wrapper">
          <div className="modal-body">{renderModalContent()}</div>
          {renderModalFooter()}
        </div>
      </DropDownModal>
    </StyledSearchFormMobile>
  );
};
